import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AuthContextValue = {
  session: Session | null;
  loading: boolean;
  hasUsername: boolean;
  onboardingStep: 5 | 6;
  refreshProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue>({
  session: null,
  loading: true,
  hasUsername: false,
  onboardingStep: 5,
  refreshProfile: async () => {},
});

function getPlaywrightTestUser(): User | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const forcePlaywright = params.has("playwright");
    if (forcePlaywright) {
      return {
        id: "00000000-0000-0000-0000-000000000000",
        email: "test@example.com",
        aud: "authenticated",
        role: "authenticated",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as unknown as User;
    }

    const raw = localStorage.getItem("playwright_test_user");
    if (raw) return JSON.parse(raw) as User;
  } catch {
    // Ignore and fall back to real auth.
  }
  return null;
}

function clearThemeVarsOnSignOut() {
  // Clear only theme system variables (prefixed with --color-).
  const root = document.documentElement;
  const themeVars = [
    "--color-primary", "--color-secondary", "--color-accent", "--color-surface", "--color-background",
    "--color-text-primary", "--color-text-secondary", "--color-text-muted",
    "--color-primary-hover", "--color-accent-hover",
    "--color-border", "--color-border-subtle",
    "--color-card", "--color-card-hover", "--color-popover", "--color-muted", "--color-input",
    "--glow-primary", "--glow-accent",
    "--gradient-primary", "--gradient-surface",
    "--decoration-type", "--particle-intensity", "--animation-speed", "--bg-decoration",
  ];
  themeVars.forEach((varName) => root.style.removeProperty(varName));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasUsername, setHasUsername] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState<5 | 6>(5);

  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const requestIdRef = useRef(0);
  const userId = session?.user?.id ?? null;

  const loadProfileState = async (profileUserId: string) => {
    const requestId = ++requestIdRef.current;

    try {
      // Prefer the explicit onboarding_complete flag when present, but fall back
      // to username presence for backwards compatibility.
      const baseQuery = supabase
        .from("profiles")
        .select("username, avatar_url, onboarding_complete")
        .eq("user_id", profileUserId)
        .maybeSingle();

      let profile: { username: string | null; avatar_url: string | null; onboarding_complete?: boolean | null } | null =
        null;

      const { data, error } = await baseQuery;
      if (error && error.code === "PGRST204" && error.message.includes("onboarding_complete")) {
        const { data: fallbackData } = await supabase
          .from("profiles")
          .select("username, avatar_url")
          .eq("user_id", profileUserId)
          .maybeSingle();

        profile = fallbackData as typeof profile;
      } else if (error) {
        throw error;
      } else {
        profile = data as typeof profile;
      }

      if (requestId !== requestIdRef.current) return;

      const username = profile?.username?.trim() ?? "";
      const isOnboarded = (profile?.onboarding_complete ?? false) || Boolean(username);
      const hasAvatar = Boolean(profile?.avatar_url);

      setHasUsername(isOnboarded);
      setOnboardingStep(hasAvatar ? 6 : 5);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setHasUsername(false);
      setOnboardingStep(5);
    }
  };

  const refreshProfile = async () => {
    if (!userId) return;
    await loadProfileState(userId);
  };
  useEffect(() => {
    let mounted = true;
    let initializing = true;
    const safetyTimer = window.setTimeout(() => {
      if (mounted) setLoading(false);
    }, 3000);

    const init = async () => {
      try {
        const testUser = getPlaywrightTestUser();
        let session: Session | null = null;
        if (testUser) {
          session = {
            access_token: "mocked_token",
            token_type: "bearer",
            expires_in: 3600,
            refresh_token: "mocked_refresh_token",
            user: testUser,
          } as Session;
        } else {
          const { data: { session: realSession } } = await supabase.auth.getSession();
          session = realSession;
        }

        if (!mounted) return;
        setSession(session);
        if (session?.user?.id) {
          await loadProfileState(session.user.id);
        } else {
          setHasUsername(false);
          setOnboardingStep(5);
        }
      } catch (e) {
        console.error("Auth init failed", e);
        if (mounted) {
          setSession(null);
          setHasUsername(false);
          setOnboardingStep(5);
        }
      } finally {
        clearTimeout(safetyTimer);
        if (mounted) {
          initializing = false;
          setLoading(false);
        }
      }
    };

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!mounted) return;
      // IMPORTANT: do not call other Supabase methods in this callback.
      // It can deadlock the client (see Supabase troubleshooting docs).
      setSession(nextSession);
      if (!nextSession) {
        setHasUsername(false);
        setOnboardingStep(5);
      } else if (nextSession.user?.id) {
        // Defer profile fetch outside the callback to avoid auth-js deadlocks.
        if (!initializing) {
          window.setTimeout(() => {
            void loadProfileState(nextSession.user.id);
          }, 0);
        }
      }
    });

    subscriptionRef.current = subscription;

    return () => {
      mounted = false;
      clearTimeout(safetyTimer);
      subscriptionRef.current?.unsubscribe();
    };
  }, []);
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const testUser = getPlaywrightTestUser();
        let session: Session | null = null;
        if (testUser) {
          session = {
            access_token: "mocked_token",
            token_type: "bearer",
            expires_in: 3600,
            refresh_token: "mocked_refresh_token",
            user: testUser,
          } as Session;
        } else {
          const { data: { session: realSession } } = await supabase.auth.getSession();
          session = realSession;
        }

        setSession(session);
        if (session?.user?.id) {
          void loadProfileState(session.user.id);
        } else {
          setHasUsername(false);
          setOnboardingStep(5);
        }
      } catch (e) {
        console.error("Auth visibility resync failed", e);
      } finally {
        setLoading(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  return (
    <AuthContext.Provider value={{ session, loading, hasUsername, onboardingStep, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
